import { SxProps, Theme, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { PWColors } from '../../Theme/configs/PWColors';
import merge from 'lodash.merge';

interface TextToggleProps {
    options: { value: string; label: string }[];
    value?: string;
    disabled?: boolean;
    sx?: {
        container?: SxProps<Theme>;
        button?: SxProps<Theme>;
    };
    onChange?: (value: string) => void;
}

const useToggleGroupStyles = (theme: Theme): { [key: string]: any } => ({
    buttonGroup: {
        gap: 0,
        border: 0,
        width: '100%',
        backgroundColor: theme.palette.mode === 'dark' ? PWColors.dark.base[600] : PWColors.light.base[200],
        borderRadius: 3,
        '.MuiToggleButtonGroup-grouped:not(:last-of-type)': {
            borderRadius: 2
        },
        '.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
            borderRadius: 2
        }
    },
    button: {
        border: 0,
        margin: '3px',
        width: '100%',
        padding: '6px',
        height: 'fit-content',
        justifyContent: 'center',
        textTransform: 'none',
        textWrap: 'wrap',
        borderRadius: 2,
        color: theme.palette.text.primary,
        '&.Mui-disabled': {
            color: theme.palette.action.inactive
        },
        '&.Mui-selected, &.Mui-selected:hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.mode === 'dark' ? PWColors.dark.base[900] : PWColors.light.base[400]
        }
    }
});

export const ToggleGroup = (props: TextToggleProps) => {
    const { options, value, disabled = false, sx, onChange } = props;

    const theme = useTheme();
    const styles = useToggleGroupStyles(theme);

    const [selection, setSelection] = useState<string>(value ?? options[0].value);

    const handleChange = (newValue: string) => {
        if (!newValue) return;

        onChange?.(newValue);
        if (!value) setSelection(newValue);
    };

    useEffect(() => {
        if (value && value !== selection) setSelection(value);
    }, [value]);

    return (
        <ToggleButtonGroup
            exclusive
            disabled={disabled}
            orientation="horizontal"
            value={selection}
            sx={merge(styles.buttonGroup, sx?.container)}
            onChange={(_, newValue) => handleChange(newValue)}>
            {options.map(option => (
                <ToggleButton key={option.value} value={option.value} sx={merge(styles.button, sx?.button)}>
                    {option.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
};
